import React, { useEffect, useRef, useState } from "react"
import { useFormik } from "formik"
import { useTracking } from "react-tracking"
import { Configurator } from "../components/configurator"
import { Preview } from "../components/preview"
import { getSubmissionTrackingString, nextLocation, pipeDataToPreview } from "./functions"
import { decodeFormData, scrollToId } from "../../modules/functions"
import { blackPearlTemplate } from "./config"
import { configuratorType } from "../components/configurator/model"
import { StickyBar } from "../components/sticky-bar"
import { SubmittingLoader } from "../components/submitting-loader"
import { useMediaQuery } from "../../modules/media-hook"
import { formikConfig } from "../../modules/formik/config"
import { defaultProfile } from "../components/configurator/components/cards/images/model"
import { fullValidationSchema } from "../../modules/formik/validation"
import { Root } from "../../components/root"

import "./style.scss"

const App = () => {
    const [template, updateTemplate] = useState(blackPearlTemplate)
    const [mobilePreviewActive, updateMobilePreviewActive] = useState(true)
    const [activeCard, setActiveCard] = useState<configuratorType>("signature")
    const [scrollPosition, setScrollPosition] = useState(false)
    const [tooltipVisible, setTooltipVisible] = useState(false)
    const [tooltipClosed, setTooltipClosed] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const siaRef = useRef() as React.MutableRefObject<HTMLDivElement>
    const isMobile = useMediaQuery("(max-width: 767.98px)")
    const { trackEvent } = useTracking()

    useEffect(() => {
        if (!tooltipClosed) {
            const id = setTimeout(() => setTooltipVisible(true), 2000)
            return () => clearTimeout(id)
        }
    })

    useEffect(() => {
        const updatePosition = () => {
            const position =
                window.innerHeight - siaRef.current?.clientHeight >= siaRef.current?.getBoundingClientRect()?.top
            if (position !== scrollPosition) {
                setScrollPosition(position)
            }
        }
        window.addEventListener("scroll", updatePosition)
        updatePosition()
        return () => window.removeEventListener("scroll", updatePosition)
    }, [toggleMobileView, siaRef])

    useEffect(() => {
        scrollToId("sia")
    }, [mobilePreviewActive, activeCard])

    const formData = decodeFormData(window.location.search)
    const formik = useFormik({
        ...formikConfig,
        initialValues: {
            ...formikConfig.initialValues,
            profileimage: defaultProfile.url,
            location: "sia",
            ...formData,
        },
        validationSchema: fullValidationSchema,
    })

    const closeTooltip = () => {
        setTooltipVisible(false)
        setTooltipClosed(true)
    }

    function toggleMobileView() {
        updateMobilePreviewActive(!mobilePreviewActive)
        closeTooltip()
    }

    function submitForm(type: "demo" | "signup") {
        setIsLoading(true)
        setActiveCard("signature")
        updateMobilePreviewActive(false)
        formik.setFieldValue("source", type).then(() => {
            formik.setFieldValue("mx", type === "demo" ? false : true).then(() => {
                formik.submitForm().then((data) => {
                    // we check if it is undefined because that means the validation has failed
                    if (data !== undefined) {
                        const trackingValue = getSubmissionTrackingString(type)
                        trackEvent({
                            event: "genericEvent",
                            category: "submit",
                            action: "submit button",
                            label: `${trackingValue}`,
                        })
                        trackEvent({ event: "leadSubmit" })
                        const isCompatible = data === "compatible"

                        window.location.href = nextLocation(isCompatible, type, formik.values)
                    } else {
                        setIsLoading(false)
                    }
                })
            })
        })

        closeTooltip()
    }

    return (
        <Root id="sia" ref={siaRef}>
            {isLoading ? (
                <SubmittingLoader />
            ) : (
                <React.Fragment>
                    <div className="sia-flex">
                        <Configurator
                            formik={formik}
                            active={activeCard}
                            setActive={setActiveCard}
                            mobileDisplay={!mobilePreviewActive || !isMobile}
                            template={template}
                            updateTemplate={updateTemplate}
                            submit={submitForm}
                        />
                        <Preview
                            mobileDisplay={mobilePreviewActive}
                            template={template}
                            signatureData={pipeDataToPreview(formik.values)}
                            submit={submitForm}
                        />
                    </div>
                    <StickyBar
                        toggleMobileView={toggleMobileView}
                        containerRef={siaRef}
                        mobileDisplay={mobilePreviewActive}
                        submit={submitForm}
                        tooltipVisible={tooltipVisible}
                        closeTooltip={closeTooltip}
                    />
                </React.Fragment>
            )}
        </Root>
    )
}

export default App
